import { useRef } from "react";
import { motion, useMotionValue, useTransform, useScroll, useSpring } from "framer-motion";
import { ArrowDown, ChevronRight } from "lucide-react";
import { PATCH_URL, IMG } from "../data/content";

const ease = [0.16, 1, 0.3, 1];

export default function Hero({ booted }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const patchScrollY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rX = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 120, damping: 16 });
  const rY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 16 });

  const onTilt = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const resetTilt = () => {
    mx.set(0);
    my.set(0);
  };

  const goTo = (href) => (e) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  const line = (text, i, cls) => (
    <span className="block overflow-hidden pb-1 -mb-1" key={text}>
      <motion.span
        className={`block ${cls}`}
        initial={{ y: "115%" }}
        animate={booted ? { y: 0 } : {}}
        transition={{ delay: 0.15 + i * 0.16, duration: 1, ease }}
      >
        {text}
      </motion.span>
    </span>
  );

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col overflow-hidden">
      <motion.div className="absolute inset-0 scale-110" style={{ y: bgY }}>
        <img src={IMG.hero} alt="Antarctic Peninsula glacier and iceberg landscape" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-abyss/80 via-midnight/55 to-midnight" />
      <div className="absolute inset-0 grid-overlay opacity-60" />
      <div className="absolute inset-0 vignette" />

      <div className="relative z-10 flex-1 flex items-center max-w-[1600px] w-full mx-auto px-4 sm:px-8 lg:px-12 pt-32 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-6 items-center w-full">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0 }}
              animate={booted ? { opacity: 1 } : {}}
              transition={{ delay: 0.1, duration: 0.8 }}
              className="font-mono text-[11px] sm:text-xs tracking-[0.3em] text-polar mb-6 flex items-center gap-3"
              data-testid="hero-eyebrow"
            >
              <span className="w-2 h-2 bg-mission pulse-dot" />
              ARJUNA SPACE AI INDIA · POLAR EARTH OBSERVATION
            </motion.div>

            <h1
              className="font-display font-extrabold uppercase leading-[0.88] tracking-tight text-[17vw] sm:text-[13vw] lg:text-[7.5rem] xl:text-[8.75rem]"
              data-testid="hero-headline"
            >
              {line("From Orbit", 0, "text-ice")}
              {line("To Ice.", 1, "text-stroke")}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={booted ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.65, duration: 0.8, ease }}
              className="mt-7 max-w-xl text-base sm:text-lg text-ice/75 leading-relaxed"
              data-testid="hero-supporting-copy"
            >
              Arjuna Antarctica is a proposed polar Earth-observation initiative exploring how satellite-derived
              environmental intelligence can be compared with authorised field observations.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={booted ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.8, duration: 0.8, ease }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <a
                href="#partners"
                onClick={goTo("#partners")}
                data-testid="hero-cta-partner"
                className="group inline-flex items-center gap-2 bg-mission text-ice font-mono text-xs uppercase tracking-[0.2em] px-7 py-4 clip-corner hover:bg-ice hover:text-midnight transition-colors duration-300"
              >
                Partner With the Mission
                <ChevronRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <a
                href="#rationale"
                onClick={goTo("#rationale")}
                data-testid="hero-cta-science"
                className="inline-flex items-center gap-2 border border-polar/40 text-polar font-mono text-xs uppercase tracking-[0.2em] px-7 py-4 clip-corner-tl hover:border-polar hover:bg-polar/10 transition-colors duration-300"
              >
                Explore the Science
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={booted ? { opacity: 1 } : {}}
              transition={{ delay: 1.05, duration: 0.8 }}
              className="mt-10 font-mono text-[10px] sm:text-[11px] tracking-[0.18em] text-tech space-y-1.5"
              data-testid="hero-telemetry"
            >
              <div>PROPOSED STUDY REGION — ANTARCTIC PENINSULA · ~63°S 059°W</div>
              <div>MISSION BASE — PUNE, INDIA · 18°31′N 073°51′E</div>
              <div>FIELD SEASON (TARGET) — DEC 2027 – 2029 · ANTARCTIC SUMMER</div>
            </motion.div>
          </div>

          <div className="lg:col-span-5 flex justify-center lg:justify-end" style={{ perspective: 900 }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.85 }}
              animate={booted ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.5, duration: 1.1, ease }}
              style={{ y: patchScrollY }}
            >
              <motion.div
                onMouseMove={onTilt}
                onMouseLeave={resetTilt}
                style={{ rotateX: rX, rotateY: rY, transformStyle: "preserve-3d" }}
                className="relative w-64 h-64 sm:w-80 sm:h-80 lg:w-[380px] lg:h-[380px] cursor-pointer"
                data-testid="hero-mission-patch"
              >
                <div className="absolute -inset-6 rounded-full border border-gold/20" />
                <svg className="absolute -inset-6 w-[calc(100%+48px)] h-[calc(100%+48px)]" viewBox="0 0 100 100" fill="none" aria-hidden="true">
                  <circle cx="50" cy="50" r="49" stroke="rgba(157,220,244,0.35)" strokeWidth="0.35" strokeDasharray="4 6" className="orbit-dash" />
                </svg>
                <div className="float-slow w-full h-full">
                  <img
                    src={PATCH_URL}
                    alt="Arjuna Antarctica polar expedition mission patch — satellite over Antarctic mountains, research vessel and penguin"
                    className="w-full h-full object-cover rounded-full ring-2 ring-gold/40 shadow-[0_0_90px_rgba(212,175,55,0.22)]"
                  />
                </div>
                <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 whitespace-nowrap font-mono text-[9px] tracking-[0.3em] text-gold/90 bg-abyss/80 border border-gold/30 px-3 py-1.5 backdrop-blur-sm">
                  SAME PLANET. BIGGER QUESTIONS.
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={booted ? { opacity: 1 } : {}}
        transition={{ delay: 1.25, duration: 0.8 }}
        className="relative z-10 border-t border-polar/10 bg-abyss/60 backdrop-blur-sm"
      >
        <div className="max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12 py-3.5 flex items-center justify-between gap-6">
          <div className="flex items-center gap-3 font-mono text-[10px] sm:text-[11px] tracking-[0.15em] text-polar/90" data-testid="hero-status-ticker">
            <span className="w-1.5 h-1.5 rounded-full bg-gold pulse-dot shrink-0" />
            <span className="truncate">
              MISSION DEVELOPMENT PHASE · RESEARCH PARTNERSHIPS, FUNDING, EXPEDITION ARRANGEMENTS AND AUTHORISATIONS IN PROGRESS
            </span>
          </div>
          <ArrowDown size={16} className="text-tech shrink-0 hidden sm:block" aria-hidden="true" />
        </div>
      </motion.div>
    </section>
  );
}
